/**
 * What Probe's Home shows: every examination area
 * by how ready it is, the actions the interviews left open, the interviews,
 * and, for one area, the moments its answers fell short. `?area=` narrows it
 * all to that area; `?show=interviews` opens every interview (the sidebar's
 * All interviews).
 *
 * @module components/apps/probe/home/probeHome
 */
import type { ProbeExaminationArea } from "@amoreg/contracts";
import {
  actionsAcross,
  practiceAreas,
  type ActionEntry,
  type AreaStanding,
  type PracticeArea,
} from "@amoreg/shared/probePractice";

import type { ProbeInterviewSummary } from "../../../../state/probeHistory";
import { interviewCovers } from "../probeArea";

/** What Home's address asks for beyond the area. */
export type HomeShow = "interviews";

/** The readiness groups, in the order Home lists them, each with its heading. */
export const STANDING_GROUPS: ReadonlyArray<{ readonly standing: AreaStanding; readonly label: string }> = [
  { standing: "needs-work", label: "Weak" },
  { standing: "thin", label: "Adequate" },
  { standing: "strong", label: "Strong" },
  { standing: "unrehearsed", label: "Not rehearsed" },
];

/** A moment an answer fell short in an area, with the interview it came from. */
export interface HomeMoment {
  readonly interviewId: string;
  readonly createdAt: string;
  readonly turn: number;
  readonly reason: string;
}

export interface HomeView {
  /** Every area, most in need of practice first. */
  readonly areas: ReadonlyArray<PracticeArea>;
  /** The area Home is narrowed to, or null for all of them. */
  readonly focused: PracticeArea | null;
  readonly standings: Readonly<Record<AreaStanding, number>>;
  /** Open actions, narrowed to the area. */
  readonly actions: ReadonlyArray<ActionEntry>;
  /** Interviews, newest first, narrowed to the area. */
  readonly interviews: ReadonlyArray<ProbeInterviewSummary>;
  /** The area's moments, newest interview first; empty with no area. */
  readonly moments: ReadonlyArray<HomeMoment>;
}

export const probeHomeOf = (
  interviews: ReadonlyArray<ProbeInterviewSummary>,
  focus: ProbeExaminationArea | null,
  isDone: (interviewId: string, text: string) => boolean,
): HomeView => {
  const areas = practiceAreas(interviews);
  const covering =
    focus === null ? interviews : interviews.filter((interview) => interviewCovers(interview, focus));
  const standings = { "needs-work": 0, thin: 0, strong: 0, unrehearsed: 0 };
  for (const entry of areas) standings[entry.standing] += 1;
  return {
    areas,
    focused: focus === null ? null : (areas.find((entry) => entry.area === focus) ?? null),
    standings,
    actions: actionsAcross(covering).filter((action) => !isDone(action.interviewId, action.text)),
    interviews: covering,
    moments:
      focus === null
        ? []
        : covering.flatMap((interview) =>
            interview.review?.status === "ready"
              ? interview.review.moments
                  .filter((moment) => moment.area === focus)
                  .map((moment) => ({
                    interviewId: interview.id,
                    createdAt: interview.createdAt,
                    turn: moment.turn,
                    reason: moment.reason,
                  }))
              : [],
          ),
  };
};

/** Each readiness group's areas, in `STANDING_GROUPS` order, empty groups left out. */
export const areasByStanding = (
  areas: ReadonlyArray<PracticeArea>,
): ReadonlyArray<{
  readonly standing: AreaStanding;
  readonly label: string;
  readonly areas: ReadonlyArray<PracticeArea>;
}> =>
  STANDING_GROUPS.map(({ standing, label }) => ({
    standing,
    label,
    areas: areas.filter((entry) => entry.standing === standing),
  })).filter((group) => group.areas.length > 0);
